import type { JSRuntimeFS } from '@/lib/JSRuntime';

export type ProjectType = 'nostr' | 'vanilla' | 'unknown';

/** Project config stored in .shakespeare/config.json */
interface ProjectConfig {
  projectType?: ProjectType;
}

/** Nostr-related package names that indicate a Nostr-powered project */
const NOSTR_PACKAGES = [
  '@nostrify/nostrify',
  '@nostrify/react',
  'nostr-tools',
  'nostr-login',
  'react-nostr-login',
  '@noble/secp256k1',
  '@scure/bip39',
  '@scure/bip32',
];

/**
 * Save project type to .shakespeare/config.json
 */
export async function saveProjectType(fs: JSRuntimeFS, projectPath: string, projectType: ProjectType): Promise<void> {
  const configDir = `${projectPath}/.shakespeare`;
  const configPath = `${configDir}/config.json`;

  try {
    // Ensure .shakespeare directory exists
    try {
      await fs.mkdir(configDir);
    } catch {
      // Directory might already exist
    }

    // Read existing config or create new one
    let config: ProjectConfig = {};
    try {
      const content = await fs.readFile(configPath, 'utf8');
      config = JSON.parse(content);
    } catch {
      // Config doesn't exist yet
    }

    // Update project type
    config.projectType = projectType;

    // Write config
    await fs.writeFile(configPath, JSON.stringify(config, null, 2));
  } catch (error) {
    console.warn('Failed to save project type:', error);
  }
}

/**
 * Get project type from .shakespeare/config.json
 */
export async function getStoredProjectType(fs: JSRuntimeFS, projectPath: string): Promise<ProjectType | null> {
  try {
    const configPath = `${projectPath}/.shakespeare/config.json`;
    const content = await fs.readFile(configPath, 'utf8');
    const config: ProjectConfig = JSON.parse(content);
    return config.projectType || null;
  } catch {
    return null;
  }
}

/**
 * Detect project type - first checks stored config, then falls back to dependency detection
 */
export async function detectProjectType(fs: JSRuntimeFS, projectPath: string): Promise<ProjectType> {
  // First, check if project type is explicitly stored
  const storedType = await getStoredProjectType(fs, projectPath);
  if (storedType) {
    return storedType;
  }

  // Fall back to dependency-based detection
  try {
    const packageJsonPath = `${projectPath}/package.json`;
    const content = await fs.readFile(packageJsonPath, 'utf8');
    const packageJson = JSON.parse(content);

    const allDependencies = {
      ...packageJson.dependencies,
      ...packageJson.devDependencies,
    };

    // Check if any Nostr-related packages are present
    const hasNostrDeps = NOSTR_PACKAGES.some(pkg => pkg in allDependencies);

    if (hasNostrDeps) {
      return 'nostr';
    }

    // If we have a package.json but no Nostr deps, it's vanilla
    return 'vanilla';
  } catch {
    // If we can't read package.json, return unknown
    return 'unknown';
  }
}

/**
 * Check if a project has specific Nostr features based on dependencies
 */
export async function detectNostrFeatures(fs: JSRuntimeFS, projectPath: string): Promise<{
  hasNostrify: boolean;
  hasNostrTools: boolean;
  hasNostrLogin: boolean;
  hasCrypto: boolean;
}> {
  try {
    const packageJsonPath = `${projectPath}/package.json`;
    const content = await fs.readFile(packageJsonPath, 'utf8');
    const packageJson = JSON.parse(content);

    const allDependencies = {
      ...packageJson.dependencies,
      ...packageJson.devDependencies,
    };

    return {
      hasNostrify: '@nostrify/nostrify' in allDependencies || '@nostrify/react' in allDependencies,
      hasNostrTools: 'nostr-tools' in allDependencies,
      hasNostrLogin: 'nostr-login' in allDependencies || 'react-nostr-login' in allDependencies,
      hasCrypto: '@noble/secp256k1' in allDependencies || '@scure/bip39' in allDependencies,
    };
  } catch {
    return {
      hasNostrify: false,
      hasNostrTools: false,
      hasNostrLogin: false,
      hasCrypto: false,
    };
  }
}
